import { createContext, useState, useContext, useEffect, useRef } from 'react'
import { AuthContext } from './AuthContext'
import { getSessionById, subscribeSession } from '../service/sessionService'

export const SessionContext = createContext()

// The first fetch is retried until it gets the session: under load (a whole class opening the
// link at once) it can time out, or come back empty when the server's database is too busy,
// and a page that only logged that stayed on "Loading Session..." until the student
// refreshed. Waits are jittered so a class does not retry in lockstep.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000]
const retryDelay = (attempt) =>
  RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)] * (0.75 + Math.random() * 0.5)
const EMPTY_ANSWER_RETRIES = 4

// A session row the page can render: the socket's DELETE push carries only { id }, and a
// failed re-fetch on the server used to push nothing at all. A full row has every column as
// a key, even the null ones.
const isFullSession = (session, sessionId) =>
  Boolean(session && typeof session === 'object' && 'type' in session && String(session.id) === String(sessionId))

export const SessionProvider = ({ children }) => {
  const [session, setSession] = useState(null)
  const [sessionLoading, setSessionLoading] = useState(true)
  // The session_id whose session the first fetch did not find (only answers without one), or
  // null: a student whose own session_id is a mistyped id (a guest who joined on that link)
  // otherwise waited on "Opening your exercise…" for good. WorkspacePage then checks the link
  // itself and says so. Cleared when the session arrives.
  const [missingSessionId, setMissingSessionId] = useState(null)
  const [link, setLink] = useState("")
  const { currentUser } = useContext(AuthContext)
  const pollRef = useRef(null)
  const userId = currentUser?.id
  const sessionId = currentUser?.session_id

  useEffect(() => {
    if (currentUser && sessionId) {
      // currentUser.session_id can change again (e.g. useSessionJoin syncing to a new
      // /workspace/:id link) before this effect's own requests resolve. Without this guard,
      // a stale in-flight fetch for the OLD session_id can resolve after cleanup and clobber
      // the correct session with outdated data - visible as a "Loading Session..." freeze
      // that only self-corrects on the next 10s poll.
      let cancelled = false
      let retryTimer = null
      const targetSessionId = sessionId

      // Whichever of the first fetch, the poll and the socket brings the session first ends
      // the loading state.
      const receive = (fresh) => {
        if (cancelled || !isFullSession(fresh, targetSessionId)) return false
        setSession(fresh)
        setSessionLoading(false)
        setMissingSessionId(null)
        return true
      }

      // Initial fetch, retried until it gets the session: failures for as long as they last,
      // answers without a session a few times only (a server that has not been updated says
      // that when its database is busy; after that it is a deleted session).
      let loaded = false
      let emptyAnswers = 0
      const fetchFirst = async (attempt) => {
        try {
          const fresh = await getSessionById(targetSessionId)
          loaded = receive(fresh) || loaded
          if (!fresh) emptyAnswers++
        } catch (err) {
          if (!cancelled) console.error(err)
        }
        if (cancelled || loaded) return
        if (emptyAnswers > EMPTY_ANSWER_RETRIES) {
          setMissingSessionId(targetSessionId)
          return
        }
        retryTimer = setTimeout(() => fetchFirst(attempt + 1), retryDelay(attempt))
      }
      fetchFirst(0)

      // Websocket subscription
      const unsubscribe = subscribeSession(targetSessionId, (fresh) => {
        loaded = receive(fresh) || loaded
      }, userId)

      // Polling fallback every 10 seconds
      pollRef.current = setInterval(async () => {
        if (cancelled) return
        try {
          loaded = receive(await getSessionById(targetSessionId)) || loaded
        } catch (err) {
          console.error('Poll fallback failed:', err)
        }
      }, 10000)

      return () => {
        cancelled = true
        clearTimeout(retryTimer)
        unsubscribe()
        clearInterval(pollRef.current)
      }
    } else if (currentUser && !sessionId) {
      setSessionLoading(true)
    }
    // Only who the user is and which session they are in: setCurrentUser runs on every join
    // (with the same session_id more often than not), and each re-run used to unsubscribe and
    // subscribe again - a write on the session row, pushed back to the whole room.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, sessionId])

  return (
    <SessionContext.Provider value={{ session, setSession, sessionLoading, missingSessionId, link, setLink }}>
      {children}
    </SessionContext.Provider>
  )
}
